import type { ReportAiContext } from '../../../functions/api/gemini/content-pipeline/types';
import { REPORT_SENTIMENT_ENABLED } from '../../../lib/report-entitlements';
import React, { useEffect, useCallback, Suspense } from 'react';
import { useSavedReport, type SavedReportState } from './useSavedReport';
import Empty from '../../common/Empty';
import { Skeleton } from '../../ui/Skeleton';
import { ThemeCards } from './ThemeCards';
import { QuoteSpotlight } from './QuoteSpotlight';
import { InsightsGrid } from './InsightsGrid';
import { ActionsPanel } from './ActionsPanel';

// Lazy-load recharts-heavy components
const SentimentJourney = React.lazy(() => import('./SentimentJourney'));
const SentimentOverview = React.lazy(() => import('./SentimentOverview'));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SentimentDataPoint {
  turnIndex: number;
  score: number;
  label: 'positive' | 'negative' | 'neutral';
  snippet: string;
  timestamp: number;
}

interface KeyMoment {
  turnIndex: number;
  label: string;
  score: number;
  timestamp: number;
}

interface ReportQuote {
  text: string;
  context: string;
  turnIndex: number;
  timestamp: number;
  sentiment?: 'positive' | 'negative' | 'neutral';
}

interface ReportFinding {
  insight: string;
  category: 'insight' | 'pain_point' | 'opportunity' | 'data_point';
}

interface ThemeAnalysis {
  theme: string;
  percentage: number;
  avgSentiment: number;
  sentimentLabel: 'positive' | 'negative' | 'neutral';
  turnIndices: number[];
}

export interface StructuredReportData {
  /** Public web context, distinct from transcript findings. */
  aiContext?: ReportAiContext[];
  /** False when the server returns only ordinary report fields. */
  advancedAnalyticsAvailable?: boolean;
  tldr: string;
  keyFindings: ReportFinding[];
  notableQuotes: ReportQuote[];
  recommendedActions: string[];
  openQuestions: string[];
  sentimentAnalysis: SentimentDataPoint[];
  keyMoments: KeyMoment[];
  thematicAnalysis: ThemeAnalysis[];
}

interface InterviewReportProps {
  /** Completed recipe outputs stay directly below the summary or report status. */
  recipeResults?: React.ReactNode;
  /** Stored results belong directly after the summary, ahead of supporting analysis. */
  children?: React.ReactNode;
  /** Reuse a report fetched by the response view for inline annotations. */
  savedReport?: SavedReportState;
  interviewId: string;
  responseId?: string;
  onNavigateToTranscript?: (turnIndex: number, timestamp?: number) => void;
  onReportDataChange?: (reportData: StructuredReportData | null) => void;
}

/**
 * Suspense fallback for lazy-loaded chart sections. Renders the SAME panel
 * chrome (rounded card, padding, border) the live chart sits in so the
 * report does not jump when the chart bundle resolves.
 */
function ChartSection({ heightClass = 'h-[340px]' }: { heightClass?: string }) {
  return (
    <div
      data-testid="report-chart-skeleton"
      aria-busy="true"
      className={`bg-surface border border-border light:border-transparent rounded-2xl p-8 ${heightClass}`}
    >
      <Skeleton height="100%" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterviewReport({
  interviewId,
  responseId,
  onNavigateToTranscript,
  onReportDataChange,
  savedReport,
  children,
  recipeResults,
}: InterviewReportProps) {
  const fetched = useSavedReport(interviewId, responseId, !savedReport);
  const { reportData, loading } = savedReport ?? fetched;

  // ---- Navigate handler ----
  const handleNavigate = useCallback(
    (turnIndex: number, timestamp?: number) => {
      onNavigateToTranscript?.(turnIndex, timestamp);
    },
    [onNavigateToTranscript],
  );

  // Signal parent when report data changes
  useEffect(() => {
    onReportDataChange?.(reportData);
  }, [reportData, onReportDataChange]);

  // The summary-or-status card (head) and, on the data path, the rest of the
  // sections (body). They stack.
  let head: React.ReactNode;
  let body: React.ReactNode = null;

  if (!loading && !reportData) {
    head = (
      <div className="bg-surface border border-border light:border-transparent rounded-2xl p-8">
        <Empty
          title="Report not ready yet"
          message="Please refresh the page in a moment to check again."
        />
      </div>
    );
  } else {
    head = (
      <div data-testid={loading && !reportData ? "report-loading-skeleton" : undefined} aria-busy={loading && !reportData || undefined} className="bg-surface border border-border light:border-transparent rounded-2xl p-8">
        <h3 className="text-base font-semibold text-foreground mb-4">Summary</h3>
        <p className="text-sm text-foreground leading-relaxed">{reportData ? reportData.tldr : <Skeleton count={2} />}</p>
      </div>
    );
  }
  if (reportData) {
    body = (
      <>
        {REPORT_SENTIMENT_ENABLED && reportData.advancedAnalyticsAvailable !== false && reportData.sentimentAnalysis.length > 0 ? <>
        {/* Sentiment Journey (lazy) */}
        <Suspense fallback={<ChartSection />}>
          <SentimentJourney
            sentimentData={reportData.sentimentAnalysis}
            keyMoments={reportData.keyMoments}
            onPointClick={handleNavigate}
          />
        </Suspense>

        {/* Sentiment Overview (lazy) */}
        <Suspense fallback={<ChartSection heightClass="h-[320px]" />}>
          <SentimentOverview sentimentData={reportData.sentimentAnalysis} />
        </Suspense>

        </> : null}

        {reportData.advancedAnalyticsAvailable !== false && reportData.thematicAnalysis.length > 0 ? <ThemeCards themes={reportData.thematicAnalysis} /> : null}

        {reportData.notableQuotes.length > 0 ? <QuoteSpotlight
          quotes={reportData.notableQuotes}
          onQuoteClick={onNavigateToTranscript ? handleNavigate : undefined}
        /> : null}

        {reportData.keyFindings.length > 0 ? <InsightsGrid findings={reportData.keyFindings} /> : null}

        <ActionsPanel
          recommendedActions={reportData.recommendedActions}
          openQuestions={reportData.openQuestions}
        />
      </>
    );
  }

  // Each section renders as its own standalone panel (no outer wrapper).
  return (
    <>
      {head}
      {recipeResults}
      {children}
      {body}
    </>
  );
}

export default InterviewReport;
